import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import AddOutlinedIcon from '@material-ui/icons/AddOutlined'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { makeStyles } from '@material-ui/core/styles'
import PgaMobileDatePicker from 'components/pga-time-picker/pga-mobile-date-picker'
import PgaMobileTimePicker from 'components/pga-time-picker/pga-mobile-time-picker'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import CustomLuxonUtils from 'lib/CustomLuxonUtils'
import { SET_COACH_AVAILABILITY_OVERRIDE } from '../custom-availability/queries'

const CREATE_BLOCK_TIME = gql`
  mutation CreateCoachAvailabilityBlockTime(
    $input: CreateCoachAvailabilityBlockTimeInput!
  ) {
    createCoachAvailabilityBlockTime(input: $input) {
      success
      message
    }
  }
`

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  dateTimeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  dateField: {
    flex: '0 0 200px',
    minWidth: 0,
  },
  dateFieldSpacer: {
    flex: '0 0 200px',
  },
  timeRangeSeparator: {
    flex: '0 0 auto',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  timeRangeRemoveSlot: {
    flex: '0 0 32px',
    display: 'flex',
    justifyContent: 'center',
  },
  addRangeButton: {
    marginTop: theme.spacing(0.5),
    paddingLeft: 0,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
}))

const blankRange = () => ({ startTime: null, endTime: null })

const isInvalidRange = r =>
  r.startTime !== null && r.endTime !== null && r.endTime <= r.startTime

const OneTimeSlotForm = ({
  facilities,
  onSuccess,
  onCancel,
  initialType = 'open',
  defaultFacilityId,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const type = initialType
  const [selectedFacilityId, setSelectedFacilityId] = useState(() => {
    if (
      defaultFacilityId &&
      facilities?.some(f => f.id === defaultFacilityId)
    ) {
      return defaultFacilityId
    }
    return facilities && facilities.length === 1 ? facilities[0].id : ''
  })
  const [date, setDate] = useState(null)
  const [ranges, setRanges] = useState([blankRange()])
  const [note, setNote] = useState('')

  const [
    setCoachAvailabilityOverride,
    { loading: savingOverride },
  ] = useMutation(SET_COACH_AVAILABILITY_OVERRIDE, {
    onCompleted: data => {
      const result = data.setCoachAvailabilityOverride
      if (result.success) {
        enqueueSnackbar('Custom availability saved', { variant: 'success' })
        onSuccess && onSuccess()
      } else {
        enqueueSnackbar(result.message || 'Failed to save', {
          variant: 'error',
        })
      }
    },
    onError: () => {
      enqueueSnackbar('Failed to save. Please try again.', { variant: 'error' })
    },
  })

  const [createBlockTime, { loading: savingBlock }] = useMutation(
    CREATE_BLOCK_TIME,
  )

  const [isBatchSubmittingBlocks, setIsBatchSubmittingBlocks] = useState(false)
  const isSubmitting = savingOverride || savingBlock || isBatchSubmittingBlocks
  const hasInvalidRange = ranges.some(isInvalidRange)
  const hasIncompleteRange = ranges.some(
    r => r.startTime === null || r.endTime === null,
  )

  const canSubmit =
    date !== null &&
    !hasIncompleteRange &&
    !hasInvalidRange &&
    !isSubmitting &&
    (type === 'block' || (type === 'open' && selectedFacilityId))

  const updateRange = (idx, key, value) => {
    setRanges(prev =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    )
  }

  const addRange = () => setRanges(prev => [...prev, blankRange()])

  const removeRange = idx => {
    setRanges(prev =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== idx),
    )
  }

  const onSubmit = async () => {
    if (type === 'open') {
      setCoachAvailabilityOverride({
        variables: {
          coachFacilityId: selectedFacilityId,
          input: {
            date: date.toISODate(),
            timeSlots: ranges.map(r => ({
              startTime: r.startTime.toFormat('HH:mm:00'),
              endTime: r.endTime.toFormat('HH:mm:00'),
            })),
          },
        },
      })
      return
    }

    const merge = new CustomLuxonUtils()
    setIsBatchSubmittingBlocks(true)
    try {
      const settled = await Promise.all(
        ranges.map(r => {
          const startDateTime = merge.mergeDateAndTime(date, r.startTime)
          const endDateTime = merge.mergeDateAndTime(date, r.endTime)
          return createBlockTime({
            variables: {
              input: {
                startTime: startDateTime,
                endTime: endDateTime,
                allDay: false,
                blockNote: note,
              },
            },
          })
            .then(({ data }) => {
              const result = data?.createCoachAvailabilityBlockTime
              return {
                ok: !!result?.success,
                message: result?.message,
              }
            })
            .catch(e => ({ ok: false, message: e.message }))
        }),
      )

      const failures = settled.filter(s => !s.ok)
      if (failures.length === 0) {
        enqueueSnackbar(
          settled.length > 1
            ? `${settled.length} blocked times saved`
            : 'Blocked time saved',
          { variant: 'success' },
        )
        onSuccess && onSuccess()
      } else if (failures.length === settled.length) {
        enqueueSnackbar(failures[0].message || 'Failed to save', {
          variant: 'error',
        })
      } else {
        enqueueSnackbar(
          `Saved ${settled.length - failures.length} of ${
            settled.length
          } blocked times; ${failures.length} failed`,
          { variant: 'warning' },
        )
        onSuccess && onSuccess()
      }
    } finally {
      setIsBatchSubmittingBlocks(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={CustomLuxonAdapter} locale="en-us">
      {type === 'open' && facilities && facilities.length > 0 && (
        <Box mb={2}>
          <Typography variant="body2" className={classes.sectionLabel}>
            Where do you want to add it?
          </Typography>
          <FormControl variant="outlined" fullWidth>
            {facilities.length === 1 ? (
              <TextField
                disabled
                value={facilities[0].name}
                variant="outlined"
              />
            ) : (
              <TextField
                select
                value={selectedFacilityId}
                variant="outlined"
                label="Facility"
                onChange={e => setSelectedFacilityId(e.target.value)}
              >
                {facilities.map(f => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FormControl>
        </Box>
      )}

      <Box mb={1.5}>
        <Typography variant="body2" className={classes.sectionLabel}>
          Date and Time
        </Typography>
        {ranges.map((r, idx) => {
          const invalid = isInvalidRange(r)
          return (
            <Box key={idx}>
              <Box className={classes.dateTimeRow}>
                {idx === 0 ? (
                  <Box className={classes.dateField}>
                    <PgaMobileDatePicker
                      label="Pick a date"
                      value={date}
                      format="MMM dd, yyyy"
                      onAccept={val => setDate(val)}
                      disablePast
                    />
                  </Box>
                ) : (
                  <Box className={classes.dateFieldSpacer} aria-hidden />
                )}
                <Box flex={1} minWidth={0}>
                  <PgaMobileTimePicker
                    id={`from-${idx}`}
                    label="From"
                    value={r.startTime}
                    validationErrors={invalid}
                    onAccept={val => updateRange(idx, 'startTime', val)}
                  />
                </Box>
                <Typography
                  variant="body2"
                  className={classes.timeRangeSeparator}
                >
                  to
                </Typography>
                <Box flex={1} minWidth={0}>
                  <PgaMobileTimePicker
                    id={`to-${idx}`}
                    label="To"
                    value={r.endTime}
                    validationErrors={invalid}
                    onAccept={val => updateRange(idx, 'endTime', val)}
                  />
                </Box>
                <Box className={classes.timeRangeRemoveSlot}>
                  {ranges.length > 1 && (
                    <IconButton
                      size="small"
                      aria-label="remove time range"
                      onClick={() => removeRange(idx)}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
              {invalid && (
                <FormHelperText error>
                  End time must be after start time
                </FormHelperText>
              )}
            </Box>
          )
        })}
        <Button
          color="primary"
          startIcon={<AddOutlinedIcon />}
          className={classes.addRangeButton}
          onClick={addRange}
        >
          Add another time range
        </Button>
      </Box>

      <Box mb={2}>
        <Typography variant="body2" className={classes.sectionLabel}>
          Note{' '}
          <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span>
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          placeholder="e.g. Virtual session, Tournament prep"
          value={note}
          onChange={e => setNote(e.target.value)}
          inputProps={{ maxLength: 60 }}
        />
      </Box>

      <Box className={classes.actions}>
        {onCancel && (
          <Button color="primary" onClick={onCancel} size="large">
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          Add Time
        </Button>
      </Box>
    </LocalizationProvider>
  )
}

export default OneTimeSlotForm
